import { yupResolver } from '@hookform/resolvers/yup';
import { DutyCode, DutyHourType } from 'generated';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { InferType } from 'yup';

import getAddEditDutyCodeSchema from '../utils/addPersonalizedDutyCodeSchema';

import { mutations, queryKeys } from 'RestAPIs/rapini';

import OverrideDutyCode from 'components/shared/DutyCodesGrid/AddPersonalizedDutyCode/components/OverrideDutyCode';
import DutyCodeBaseFields from 'components/shared/DutyCodesGrid/DutyCodeBaseFields';
import { Dialog, LoadingOverlay, showSnackbar } from 'components/shared/index';

import Actions from '../../Actions';
import { IconTooltip } from 'components/shared/IconTooltip';
import { Box } from '@mui/material';

type Props = {
  isOpen: boolean;
  handleClose: () => void;
  onSave: () => void;
  organizationalUnitId?: number;
};

export type DutyCodePeriodForm = {
  id: number;
  dutyHourTypeId: number;
  startTime: Date;
  endTime: Date;
  dutyHourType: DutyHourType;
};

const schema = getAddEditDutyCodeSchema();
export type DutyCodeForm = InferType<typeof schema>;

const defaultFormValues: DutyCodeForm = {
  displayDutyCode: '',
  colorCode: '',
  overrideDutyCodeId: -1
};

const AddPersonalizedDutyCode = ({
  isOpen,
  handleClose,
  onSave,
  organizationalUnitId
}: Props) => {
  const { t } = useTranslation('common');
  const methods = useForm<DutyCodeForm>({
    defaultValues: defaultFormValues,
    resolver: yupResolver(getAddEditDutyCodeSchema())
  });

  const { control, handleSubmit, reset, setValue } = methods;
  const { mutate: createDutyCode, isLoading: isCreating } =
    mutations.useCreateDutyCode();
  const QueryClient = useQueryClient();

  function handleReset() {
    reset(defaultFormValues);
  }

  function onSubmit(data: DutyCodeForm) {
    if (data.overrideDutyCodeId && data.overrideDutyCodeId > 0) {
      createDutyCode(
        {
          dutyCodeId: data.overrideDutyCodeId,
          code: data.displayDutyCode,
          colorCode: data.colorCode,
          organizationalUnitId: organizationalUnitId
        },
        {
          onSuccess: () => {
            QueryClient.invalidateQueries(queryKeys.getDutyCodes.name);
            onSave();
            handleReset();
          },
          onError: (error: any) => {
            const message = t(
              error?.response?.data?.error?.message || 'somethingWentWrong'
            );
            showSnackbar({
              severity: 'error',
              message: message
            });
          }
        }
      );
    }
  }

  function onOverrideDutyCodeSelect(dutyCode?: DutyCode) {
    setValue('overrideDutyCodeId', dutyCode?.dutyCodeId || -1);
    setValue('displayDutyCode', dutyCode?.dutyCode || '');
  }

  function onDialogClose() {
    handleClose();
    handleReset();
  }

  return (
    <Dialog
      open={isOpen}
      title={t('addPersonalizedDutyCode')}
      handleClose={onDialogClose}
      PaperProps={{
        sx: {
          minWidth: 550
        }
      }}
    >
      <FormProvider {...methods}>
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <Box display={'flex'}>
            <OverrideDutyCode
              control={control}
              onDutyCodeSelect={onOverrideDutyCodeSelect}
            />
            <IconTooltip
              fontSize="small"
              title={t('dutyCodeTooltip')}
              placement="top-end"
            />
          </Box>
          <DutyCodeBaseFields />
          <Actions onDialogClose={onDialogClose} />
          <LoadingOverlay loading={isCreating} />
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AddPersonalizedDutyCode;
